/**
 * SuperAdminTicketsPanel — inbox for support tickets.
 * Premium tickets float to the top (priority_score sorted).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, MessageSquare, Crown, Send } from "lucide-react";

const STATUS_TABS = ["open", "in_progress", "resolved"];

export default function SuperAdminTicketsPanel() {
  const [tab, setTab] = useState("open");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [replyDraft, setReplyDraft] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/church-builder/tickets", { params: { status: tab } });
      setItems(data);
    } catch { toast.error("Could not load tickets"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (expanded) load(); /* eslint-disable-next-line */ }, [tab, expanded]);

  const update = async (id, patch) => {
    try {
      await api.patch(`/admin/church-builder/tickets/${id}`, patch);
      toast.success("Updated");
      load();
    } catch { toast.error("Update failed"); }
  };

  return (
    <div data-testid="cb-admin-tickets-panel" className="luxury-card rounded-xl p-5 mt-6 border border-[#D4AF37]/15">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <MessageSquare size={16} className="text-[#D4AF37]" />
          <div>
            <div className="font-serif-display text-xl">Support Tickets</div>
            <div className="text-xs text-neutral-400 mt-0.5">Premium tickets are flagged as <Crown size={10} className="inline text-[#D4AF37]" /> PRIORITY and float to the top.</div>
          </div>
        </div>
        <button
          type="button"
          data-testid="cb-admin-tickets-toggle"
          onClick={() => setExpanded((p) => !p)}
          className="btn-ghost-gold px-3 py-2 rounded text-[10px] tracking-widest uppercase"
        >{expanded ? "Hide" : "Show"} inbox</button>
      </div>

      {expanded && (
        <div className="mt-5">
          <div className="inline-flex border border-white/10 rounded-full p-1 mb-4">
            {STATUS_TABS.map((s) => (
              <button key={s} data-testid={`cb-admin-tickets-tab-${s}`} onClick={() => setTab(s)}
                className={`px-3 py-1.5 text-[10px] tracking-widest uppercase rounded-full ${tab === s ? "bg-[#D4AF37] text-black" : "text-neutral-400"}`}>
                {s.replace("_", " ")}
              </button>
            ))}
          </div>
          {loading ? <Loader2 className="animate-spin text-[#D4AF37]" size={16} /> :
            items.length === 0 ? <div className="text-xs text-neutral-500">No {tab} tickets.</div> :
            <div className="space-y-3">
              {items.map((t) => (
                <div key={t.id} data-testid={`cb-admin-ticket-${t.id}`} className={`rounded-lg border p-4 ${t.is_priority ? "border-[#D4AF37]/40 bg-[#D4AF37]/5" : "border-white/10 bg-white/[0.02]"}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {t.is_priority && <span className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase font-bold text-[#D4AF37]"><Crown size={10} /> Priority</span>}
                        <div className="text-sm text-white font-semibold">{t.subject}</div>
                      </div>
                      <div className="text-[11px] text-neutral-500 mt-1">
                        {t.church_name} (/{t.church_slug}) · {t.submitted_by_email} · {t.plan_id} · {t.category} · {new Date(t.created_at).toLocaleString()}
                      </div>
                      <p className="text-neutral-200 text-sm mt-3 whitespace-pre-wrap leading-relaxed">{t.message}</p>
                      {t.admin_reply && (
                        <div className="mt-3 rounded border border-emerald-500/30 bg-emerald-900/10 p-3">
                          <div className="text-[10px] tracking-widest uppercase text-emerald-300">Your reply · {t.replied_at ? new Date(t.replied_at).toLocaleString() : ""}</div>
                          <p className="text-neutral-200 text-sm mt-1 whitespace-pre-wrap">{t.admin_reply}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 space-y-2">
                    <textarea
                      rows={2}
                      placeholder="Reply (saved to ticket; we'll email reply separately if needed)"
                      value={replyDraft[t.id] ?? ""}
                      onChange={(e) => setReplyDraft({ ...replyDraft, [t.id]: e.target.value })}
                      className="w-full bg-black border border-white/15 focus:border-[#D4AF37] outline-none px-3 py-2 rounded text-xs text-white"
                      data-testid={`cb-admin-ticket-reply-${t.id}`}
                    />
                    <div className="flex gap-2 flex-wrap">
                      <button data-testid={`cb-admin-ticket-status-progress-${t.id}`} onClick={() => update(t.id, { status: "in_progress", admin_reply: replyDraft[t.id] || null })} className="btn-ghost-gold px-3 py-1.5 rounded text-[10px] tracking-widest uppercase">Mark in-progress</button>
                      <button data-testid={`cb-admin-ticket-status-resolved-${t.id}`} onClick={() => update(t.id, { status: "resolved", admin_reply: replyDraft[t.id] || null })} className="btn-gold px-3 py-1.5 rounded text-[10px] tracking-widest uppercase font-semibold">Resolve</button>
                      <button data-testid={`cb-admin-ticket-reply-save-${t.id}`} onClick={() => update(t.id, { status: t.status, admin_reply: replyDraft[t.id] || null })} className="text-[10px] tracking-widest uppercase text-neutral-400 hover:text-[#D4AF37] inline-flex items-center gap-1"><Send size={11} /> Save reply</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          }
        </div>
      )}
    </div>
  );
}
